import { useSessionContext } from '../context/session-context'
import { coreService } from '../service/core-service'
import { logger } from '../util/logger'
import { normalizeUsername, parseUsernameUpdateError } from '../util/username-update-util'
import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'

interface UseUpdateUsernameOptions {
  onError: (field: 'username', error: { type: string; message: string }) => void
  onSuccess: (username?: string) => void
}

export const useUpdateUsername = (
  options: UseUpdateUsernameOptions,
): {
  updateUsername: (username: string) => void
  isUpdating: boolean
} => {
  const { refreshMyData } = useSessionContext()

  const { mutate: updateUsernameMutation, isLoading: isUpdating } = useMutation<void, Error, string>(
    async (username: string): Promise<void> => {
      const normalized = normalizeUsername(username)
      await coreService.updateMe({
        profile: { username: normalized },
      })
    },
    {
      onSuccess: async (_, username) => {
        const normalized = normalizeUsername(username)
        await refreshMyData()
        options.onSuccess(normalized)
      },
      onError: (error: Error) => {
        logger.error('Error updating username:', error)
        const message = parseUsernameUpdateError(error, 'Failed to update username')
        options.onError('username', { type: 'server', message })
      },
    },
  )

  const updateUsername = useCallback(
    (username: string) => {
      updateUsernameMutation(username)
    },
    [updateUsernameMutation],
  )

  return {
    updateUsername,
    isUpdating,
  }
}
